import { useState, useEffect, useRef } from 'react';
import { APP_VERSION, CHANNEL, DEV_MODE, VERSION_LABEL } from '../config.js';
import { getKey } from '../data/crypto.js';
import * as store from '../data/store.js';
import { addEntry, createCloudWriter, removeEntry, restoreEntry, startSync, updateEntry } from '../data/sync.js';
import { MOCK_DATA } from '../data/mockData.js';
import { Book, Calculator, Settings, Thermometer } from '../components/icons.jsx';
import { Toaster } from '../components/Toaster.jsx';
import { ChangelogModal } from '../components/ChangelogModal.jsx';
import { CalculatorView } from './CalculatorView.jsx';
import { TemperatureView } from './TemperatureView.jsx';
import { HistoryView } from './HistoryView.jsx';
import { SettingsView } from './SettingsView.jsx';

// share one row with it on a phone.
const VIEW_META = {
  temperature: { title: 'Temperature', icon: Thermometer },
  calculator: { title: 'Dosage Calculator', icon: Calculator },
  history: { title: 'Records', icon: Book },
  settings: { title: 'Settings', icon: Settings },
};

// Tab-bar order, for the sliding highlight's position.
const TAB_VIEWS = ['temperature', 'calculator', 'history', 'settings'];

// The class that plays each tab-switch animation (keyframes in styles.css).
const TAB_ANIMATION_CLASS = { none: '', fade: 'tab-enter-fade', rise: 'tab-enter-rise' };

export function MedicationApp({ onLogout }) {
  const [activeView, setActiveView] = useState('temperature');
  const [medication, setMedication] = useState('paracetamol');
  const [weight, setWeight] = useState('');
  const getStoredDefaultChild = () => {
    try { return localStorage.getItem('medDefaultChild') || ''; } catch { return ''; }
  };
  // The one "which child" for the whole app, picked in the banner. Remembered
  // per-device (like dark mode) so each phone can default to a different child.
  const [defaultChildId, setDefaultChild] = useState(getStoredDefaultChild);
  const [concentration, setConcentration] = useState('100/1');
  const [result, setResult] = useState(null);
  // Appearance: 'system' follows the phone's light/dark setting; 'light' and
  // 'dark' pin it. A phone that already chose with the old Dark/Light Mode
  // toggle (medDarkMode) keeps that choice; a fresh one follows the system.
  const [themePref, setThemePref] = useState(() => {
    try {
      const t = localStorage.getItem('medTheme');
      if (t === 'system' || t === 'light' || t === 'dark') return t;
      const legacy = localStorage.getItem('medDarkMode');
      return legacy === null ? 'system' : legacy === 'true' ? 'dark' : 'light';
    } catch { return 'system'; }
  });
  const darkQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const [systemDark, setSystemDark] = useState(() => (darkQuery ? darkQuery.matches : true));
  useEffect(() => {
    if (!darkQuery) return;
    const onChange = (e) => setSystemDark(e.matches);
    darkQuery.addEventListener('change', onChange);
    return () => darkQuery.removeEventListener('change', onChange);
  }, []);
  const darkMode = themePref === 'system' ? systemDark : themePref === 'dark';
  // Tab-switch animation, per device: 'rise' (fade in while rising a few px,
  // with the tab bar's highlight sliding across), 'fade', or 'none'. A phone
  // set to reduce motion starts on none.
  const [animationPref, setAnimationPref] = useState(() => {
    try {
      const a = localStorage.getItem('medAnimation');
      if (a === 'none' || a === 'fade' || a === 'rise') return a;
    } catch {}
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'none' : 'rise';
  });
  const [childrenList, setChildrenList] = useState([]);
  const [dosageHistory, setDosageHistory] = useState([]);
  const [temperatureLog, setTemperatureLog] = useState([]);
  const [customConcentrations, setCustomConcentrations] = useState({ paracetamol: [], ibuprofen: [] });
  const [otherMedications, setOtherMedications] = useState([{ name: 'Antibiotics', category: 'Other' }]);
  const [undoStack, setUndoStack] = useState([]);
  const [syncStatus, setSyncStatus] = useState('loading'); // loading | synced | offline | error
  // Writes every change to the cloud (data/sync.js); in DEV_MODE it skips
  // them — there's no session/key to encrypt with, and mock edits shouldn't
  // touch the real backend anyway.
  const cloudRef = useRef(null);
  if (!cloudRef.current) cloudRef.current = createCloudWriter(DEV_MODE ? null : store, store.syncFail);
  const cloud = cloudRef.current;

  // Auto-show "What's New" when this device opens a version newer than the last
  // one it saw (tracked per-device, like dark mode). A brand-new device (no key
  // yet) is treated as caught up so a first-time user isn't shown the full
  // history — the mount effect below records the current version for it.
  const [showChangelog, setShowChangelog] = useState(() => {
    try {
      const seen = localStorage.getItem('medLastSeenVersion');
      return seen !== null && seen !== APP_VERSION;
    } catch { return false; }
  });
  useEffect(() => {
    try {
      if (localStorage.getItem('medLastSeenVersion') === null) {
        localStorage.setItem('medLastSeenVersion', APP_VERSION);
      }
    } catch (e) {}
  }, []);
  // Each tab starts at the top: switching used to keep the previous tab's
  // scroll, so leaving Records scrolled down opened the Calculator mid-page.
  useEffect(() => { window.scrollTo(0, 0); }, [activeView]);
  // Reopening the app from the background resumes the page wherever it was
  // left. After more than a minute away, treat it as a fresh open and go back
  // to the top; a quick glance at another app keeps your place.
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt !== null && Date.now() - hiddenAt > 60000) window.scrollTo(0, 0);
      hiddenAt = null;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  const dismissChangelog = () => {
    setShowChangelog(false);
    try { localStorage.setItem('medLastSeenVersion', APP_VERSION); } catch (e) {}
  };

  // Load from the encrypted cloud store and keep both phones in sync via realtime.
  // Falls back to the offline ciphertext cache when the network is unavailable.
  useEffect(() => {
    if (DEV_MODE) {
      setChildrenList(MOCK_DATA.children);
      setDosageHistory(MOCK_DATA.history);
      setTemperatureLog(MOCK_DATA.temperatures);
      setCustomConcentrations(MOCK_DATA.concentrations);
      setOtherMedications(MOCK_DATA.otherMeds);
      setSyncStatus('synced');
      return;
    }
    return startSync({
      store,
      apply: (d) => {
        setChildrenList(d.children);
        setDosageHistory(d.history);
        setTemperatureLog(d.temperatures);
        setCustomConcentrations(d.concentrations);
        setOtherMedications(d.otherMeds);
      },
      setStatus: setSyncStatus,
      canMigrate: () => !!getKey(),
    });
  }, []);

  useEffect(() => {
    try { localStorage.setItem('medTheme', themePref); } catch (e) {}
  }, [themePref]);

  useEffect(() => {
    try { localStorage.setItem('medAnimation', animationPref); } catch (e) {}
  }, [animationPref]);

  useEffect(() => {
    try { localStorage.setItem('medDefaultChild', defaultChildId); } catch (e) {}
  }, [defaultChildId]);

  // Each change updates the screen first, then writes it to the cloud. The
  // list operations and the write rules are in data/sync.js.
  const saveData = {
    children: (data) => {
      const before = childrenList;
      setChildrenList(data);
      cloud.saveChildren(before, data);
    },
    concentrations: (data) => { setCustomConcentrations(data); cloud.saveConcentrations(data); },
    otherMeds: (data) => { setOtherMedications(data); cloud.saveOtherMeds(data); },
  };

  const addHistory = (entry) => {
    setDosageHistory(prev => addEntry(prev, entry));
    cloud.addHistory(entry);
  };
  const deleteHistory = (id) => {
    setDosageHistory(prev => removeEntry(prev, id));
    cloud.deleteEntry(id);
  };
  // Undo of a delete from Records.
  const restoreHistory = (entry) => {
    setDosageHistory(prev => restoreEntry(prev, entry));
    cloud.restoreHistory(entry);
  };
  // A corrected entry overwrites its own row. The row is written from the
  // list as this render has it; an id that isn't there writes nothing.
  const updateHistory = (id, updates) => {
    const current = dosageHistory.find(e => e.id === id);
    setDosageHistory(prev => updateEntry(prev, id, updates));
    if (current) cloud.updateHistory({ ...current, ...updates });
  };

  // Temperature readings mirror history.
  const addTemperature = (entry) => {
    setTemperatureLog(prev => addEntry(prev, entry));
    cloud.addTemperature(entry);
  };
  const deleteTemperature = (id) => {
    setTemperatureLog(prev => removeEntry(prev, id));
    cloud.deleteEntry(id);
  };
  const restoreTemperature = (entry) => {
    setTemperatureLog(prev => restoreEntry(prev, entry));
    cloud.restoreTemperature(entry);
  };
  const updateTemperature = (id, updates) => {
    const current = temperatureLog.find(e => e.id === id);
    setTemperatureLog(prev => updateEntry(prev, id, updates));
    if (current) cloud.updateTemperature({ ...current, ...updates });
  };

  const undoLastDose = () => {
    if (undoStack.length === 0) return;
    const lastAction = undoStack[undoStack.length - 1];
    // Several "Other" medicines logged in one action are one undo item; the
    // fallback covers items pushed by the single-entry paths.
    const ids = lastAction.entryIds || [lastAction.entryId];
    ids.forEach(deleteHistory);
    setUndoStack(undoStack.slice(0, -1));
  };

  const ViewIcon = VIEW_META[activeView].icon;
  // A remembered child that has since been deleted counts as none. Derived
  // rather than written back, so it can't wipe the choice while the children
  // list is still loading.
  const activeChildId = childrenList.some(c => c.id === defaultChildId) ? defaultChildId : '';

  return (
    <div className={`min-h-screen px-3 pt-3 pb-24 sm:px-4 sm:pt-4 overflow-x-hidden ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
      <div className="max-w-4xl mx-auto sm:mt-4">
        {DEV_MODE && (
          <div className="mb-3 p-2 rounded-lg text-sm text-center font-semibold bg-purple-900 text-purple-200 border border-purple-700">
            DEV MODE — mock data, not connected to Supabase
          </div>
        )}
        {syncStatus === 'offline' && (
          <div className="mb-3 p-2 rounded-lg text-sm text-center bg-yellow-900 text-yellow-200">
            Offline — showing saved data. New changes may not sync until you reconnect.
          </div>
        )}
        {syncStatus === 'error' && (
          <div className="mb-3 p-2 rounded-lg text-sm text-center bg-red-900 text-red-200">
            Couldn't reach the server. <button onClick={() => location.reload()} className="underline font-semibold">Retry</button>
          </div>
        )}
        {/* One slim banner for every tab: what you're looking at, and which
            child it's for. The child picked here drives every tab — there is
            no second "which child" picker inside the forms. */}
        <div className={`flex items-center gap-2 mb-3 pl-2 pr-1 py-1 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
          <div className="bg-indigo-100 p-1.5 rounded-full flex-shrink-0">
            <ViewIcon className="w-4 h-4 text-indigo-600" />
          </div>
          <h1 className="flex-1 min-w-0 text-base font-bold truncate">{VIEW_META[activeView].title}</h1>
          {childrenList.length > 0 && (
            <select value={activeChildId} onChange={(e) => setDefaultChild(e.target.value)}
              aria-label="Child"
              className={`flex-shrink-0 max-w-[45%] px-2 py-1.5 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 ${
                darkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'
              }`}>
              {/* Same "no child" value everywhere; only Records can use it,
                  as "show everyone". */}
              <option value="">{activeView === 'history' ? 'All children' : 'Choose child'}</option>
              {childrenList.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Keyed by tab, so switching remounts it and replays the entry
            animation (the screens below already remount on a switch). Only
            the content moves; the banner and tab bar stay put. */}
        <div key={activeView} className={TAB_ANIMATION_CLASS[animationPref]}>
        {activeView === 'temperature' && (
          <TemperatureView
            darkMode={darkMode}
            childrenList={childrenList}
            temperatureLog={temperatureLog}
            dosageHistory={dosageHistory}
            addTemperature={addTemperature}
            deleteTemperature={deleteTemperature}
            restoreTemperature={restoreTemperature}
            defaultChildId={activeChildId}
          />
        )}

        {activeView === 'calculator' && (
          <CalculatorView
            darkMode={darkMode}
            medication={medication}
            setMedication={setMedication}
            weight={weight}
            setWeight={setWeight}
            selectedChild={activeChildId}
            setSelectedChild={setDefaultChild}
            childrenList={childrenList}
            concentration={concentration}
            setConcentration={setConcentration}
            customConcentrations={customConcentrations}
            result={result}
            setResult={setResult}
            dosageHistory={dosageHistory}
            temperatureLog={temperatureLog}
            saveData={saveData}
            addHistory={addHistory}
            otherMedications={otherMedications}
            undoStack={undoStack}
            setUndoStack={setUndoStack}
          />
        )}

        {activeView === 'history' && (
          <HistoryView
            darkMode={darkMode}
            dosageHistory={dosageHistory}
            temperatureLog={temperatureLog}
            childrenList={childrenList}
            otherMedications={otherMedications}
            onDelete={(id) => {
              deleteHistory(id);
              // Only the undo item for that dose goes (its Undo would delete
              // nothing); deleting an older record leaves the last-registered
              // dose's Undo in place.
              setUndoStack(stack => stack.filter(a => !(a.entryIds || [a.entryId]).includes(id)));
            }}
            onDeleteTemperature={deleteTemperature}
            onRestore={restoreHistory}
            onRestoreTemperature={restoreTemperature}
            onUpdate={updateHistory}
            onUpdateTemperature={updateTemperature}
            undoStack={undoStack}
            onUndo={undoLastDose}
            defaultChildId={activeChildId}
          />
        )}

        {activeView === 'settings' && (
          <SettingsView
            darkMode={darkMode}
            childrenList={childrenList}
            customConcentrations={customConcentrations}
            otherMedications={otherMedications}
            saveData={saveData}
            onShowChangelog={() => setShowChangelog(true)}
            themePref={themePref}
            setThemePref={setThemePref}
            animationPref={animationPref}
            setAnimationPref={setAnimationPref}
            onLogout={onLogout}
          />
        )}
        </div>

        {/* Sign out and appearance live in Settings → This phone now, rather
            than under every tab. */}
        <p className={`text-center text-xs mt-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          MedCalc {CHANNEL === 'production' ? VERSION_LABEL : (
            // Amber on non-production so "this isn't the real app" reads at a
            // glance, without needing to parse the string.
            <span className={darkMode ? 'text-amber-500' : 'text-amber-600'}>{VERSION_LABEL}</span>
          )}
        </p>
      </div>

      {/* Tab bar pinned to the bottom like a phone app: in thumb reach, and
          it no longer pushes the form down. Padding clears the iPhone home
          indicator. */}
      <nav className={`fixed bottom-0 inset-x-0 z-40 border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="relative max-w-4xl mx-auto flex">
          {/* With animation on, one highlight glides to the tapped tab instead
              of each tab drawing its own. Sized and placed exactly like the
              per-tab one: a 52×28 pill, 8 px from the top, centred in its
              quarter of the bar. */}
          {animationPref !== 'none' && (
            <div aria-hidden="true"
              className="absolute top-2 left-0 w-1/4 flex justify-center pointer-events-none transition-transform duration-200 ease-out"
              style={{ transform: `translateX(${TAB_VIEWS.indexOf(activeView) * 100}%)` }}>
              <span className={`w-[52px] h-7 rounded-full ${darkMode ? 'bg-indigo-900' : 'bg-indigo-100'}`} />
            </div>
          )}
          {[
            ['temperature', Thermometer, 'Temperature'],
            ['calculator', Calculator, 'Calculator'],
            ['history', Book, 'Records'],
            ['settings', Settings, 'Settings'],
          ].map(([view, Icon, label]) => (
            <button key={view} onClick={() => setActiveView(view)}
              aria-current={activeView === view ? 'page' : undefined}
              className={`relative flex-1 min-w-0 flex flex-col items-center gap-0.5 pt-2 pb-1.5 text-xs font-medium transition ${
                activeView === view ? 'text-indigo-500' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
              }`}>
              <span className={`px-4 py-1 rounded-full ${activeView === view && animationPref === 'none' ? (darkMode ? 'bg-indigo-900' : 'bg-indigo-100') : ''}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </nav>
      <Toaster darkMode={darkMode} />
      {showChangelog && <ChangelogModal darkMode={darkMode} onClose={dismissChangelog} />}
    </div>
  );
}
